// ═══════════════════════════════════════════════════════════
// POINT PICKUP MANAGER - Score Collectibles
// ═══════════════════════════════════════════════════════════

import { Vector2, Bounds, PointPickup } from './types.js';
import { Utils } from './Utils.js';
import { Theme, getPickupTier, applyNeonGlow, clearNeonGlow } from './Theme.js';

export class PointPickupManager {
    private pickups: PointPickup[] = [];
    private bounds: Bounds;
    private spawnTimer: number = 0;
    private spawnInterval: number = 4; // Spawn a pickup every 4 seconds
    private maxPickups: number = 5; // Maximum pickups on screen at once

    constructor(bounds: Bounds) {
        this.bounds = bounds;
    }

    update(deltaTime: number): void {
        // Update existing pickups (animation) - enhanced speeds from theme
        for (const pickup of this.pickups) {
            pickup.pulsePhase += deltaTime * Theme.pickups.pulseSpeed;
            pickup.rotationAngle += deltaTime * Theme.pickups.spinSpeed;
            pickup.glowIntensity = 0.6 + Math.sin(pickup.pulsePhase) * 0.4;
        }

        // Spawn new pickups periodically
        this.spawnTimer += deltaTime;
        if (this.spawnTimer >= this.spawnInterval && this.pickups.length < this.maxPickups) {
            this.spawnPickup();
            this.spawnTimer = 0;
        }
    }

    private spawnPickup(): void {
        // Padding from edges to avoid spawning too close to walls
        const padding = 50;

        // Random position anywhere on screen (with padding)
        const position: Vector2 = {
            x: Utils.random(padding, this.bounds.width - padding),
            y: Utils.random(padding, this.bounds.height - padding)
        };

        // Random point values with different rarities
        const rand = Math.random();
        let value: number;
        let size: number;

        if (rand < 0.6) {
            // 60% chance: Small pickup (10 points)
            value = 10;
            size = 15;
        } else if (rand < 0.9) {
            // 30% chance: Medium pickup (25 points)
            value = 25;
            size = 20;
        } else {
            // 10% chance: Large pickup (50 points)
            value = 50;
            size = 25;
        }

        this.pickups.push({
            position,
            size,
            value,
            pulsePhase: Utils.random(0, Math.PI * 2),
            glowIntensity: 1,
            rotationAngle: Utils.random(0, Math.PI * 2)
        });
    }

    checkCollision(ballPosition: Vector2, ballSize: number): PointPickup | null {
        const ballRadius = ballSize / 2;

        for (let i = this.pickups.length - 1; i >= 0; i--) {
            const pickup = this.pickups[i];
            const distance = Math.sqrt(
                Math.pow(ballPosition.x - pickup.position.x, 2) +
                Math.pow(ballPosition.y - pickup.position.y, 2)
            );

            // Check if ball collects the pickup
            if (distance < ballRadius + pickup.size / 2) {
                // Remove the pickup and return it
                const collected = this.pickups.splice(i, 1)[0];
                return collected;
            }
        }

        return null;
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const pickup of this.pickups) {
            this.renderPickup(ctx, pickup);
        }
    }

    private renderPickup(ctx: CanvasRenderingContext2D, pickup: PointPickup): void {
        const offsetY = this.bounds.offsetY || 0;
        ctx.save();
        ctx.translate(pickup.position.x, pickup.position.y + offsetY);

        // Get tier-specific colors and sizes from theme
        const tier = getPickupTier(pickup.value);

        // Pulsing scale effect (enhanced)
        const pulseScale = 1 + Math.sin(pickup.pulsePhase) * Theme.pickups.pulseAmount;
        ctx.scale(pulseScale, pulseScale);

        // Rotate the shard
        ctx.rotate(pickup.rotationAngle);

        const size = tier.size;

        // Draw sparkle particles around the shard
        this.renderSparkles(ctx, pickup, size);

        // Draw outer neon glow layers
        this.renderEnergyGlow(ctx, tier.color, tier.glow, size, pickup.glowIntensity);

        // Draw energy shard shape (diamond/crystal)
        this.renderCrystalShape(ctx, tier.color, size);

        // Draw glowing outline
        applyNeonGlow(ctx, tier.color, Theme.glow.pickupGlow);
        ctx.strokeStyle = Theme.colors.glowWhite;
        ctx.lineWidth = 2;
        ctx.stroke();
        clearNeonGlow(ctx);

        // Draw inner energy core
        this.renderEnergyCore(ctx, size);

        // Draw point value text for larger pickups
        if (pickup.value >= 25) {
            ctx.rotate(-pickup.rotationAngle); // Counter-rotate text to keep it upright
            ctx.font = 'bold 12px "Courier New", monospace';

            // Text with neon glow
            applyNeonGlow(ctx, Theme.colors.glowWhite, 15);
            ctx.fillStyle = Theme.colors.glowWhite;
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 3;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const text = `+${pickup.value}`;
            ctx.strokeText(text, 0, 0);
            ctx.fillText(text, 0, 0);

            clearNeonGlow(ctx);
        }

        ctx.restore();
    }

    /**
     * Render sparkle particles orbiting the shard
     */
    private renderSparkles(ctx: CanvasRenderingContext2D, pickup: PointPickup, size: number): void {
        const sparkleCount = Theme.pickups.sparkleCount;
        const sparkleOrbitRadius = size * 1.5;

        for (let i = 0; i < sparkleCount; i++) {
            const angle = (i / sparkleCount) * Math.PI * 2 + pickup.rotationAngle * 0.5;
            const x = Math.cos(angle) * sparkleOrbitRadius;
            const y = Math.sin(angle) * sparkleOrbitRadius;

            const sparkleSize = 2 + Math.sin(pickup.pulsePhase + i) * 1;
            const alpha = 0.6 + Math.sin(pickup.pulsePhase * 2 + i) * 0.4;

            // Random color from sparkle palette
            const colorIndex = i % Theme.pickups.sparkleColors.length;
            const color = Theme.pickups.sparkleColors[colorIndex];

            ctx.save();
            ctx.globalAlpha = alpha;

            // Draw sparkle glow
            const gradient = ctx.createRadialGradient(x, y, 0, x, y, sparkleSize * 2);
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);

            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.6)`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(x - sparkleSize * 2, y - sparkleSize * 2, sparkleSize * 4, sparkleSize * 4);

            ctx.restore();
        }
    }

    /**
     * Render energy glow around the shard
     */
    private renderEnergyGlow(
        ctx: CanvasRenderingContext2D,
        color: string,
        glowColor: string,
        size: number,
        intensity: number
    ): void {
        // Multiple glow layers for intense neon effect
        for (let i = 0; i < 3; i++) {
            const glowRadius = size * (2 + i * 0.5);
            const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);

            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);

            const alpha = (intensity * 0.6 - i * 0.15);
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(-glowRadius, -glowRadius, glowRadius * 2, glowRadius * 2);
        }
    }

    /**
     * Render crystal/shard shape
     */
    private renderCrystalShape(ctx: CanvasRenderingContext2D, color: string, size: number): void {
        // Draw diamond/crystal shape
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size, 0);
        ctx.lineTo(0, size);
        ctx.lineTo(-size, 0);
        ctx.closePath();

        // Gradient fill for depth
        const gradient = ctx.createRadialGradient(-size * 0.3, -size * 0.3, 0, 0, 0, size);

        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);

        gradient.addColorStop(0, `rgba(255, 255, 255, 0.9)`); // Bright center
        gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, 1)`);
        gradient.addColorStop(0.7, `rgba(${r * 0.7}, ${g * 0.7}, ${b * 0.7}, 1)`);
        gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 1)`);

        ctx.fillStyle = gradient;
        ctx.fill();
    }

    /**
     * Render bright energy core in center
     */
    private renderEnergyCore(ctx: CanvasRenderingContext2D, size: number): void {
        const coreSize = size * 0.4;

        // Bright white core
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, coreSize);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.7)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(-coreSize, -coreSize, coreSize * 2, coreSize * 2);
    }

    reset(): void {
        this.pickups = [];
        this.spawnTimer = 0;
    }

    // Spawn initial pickups at game start
    spawnInitial(count: number = 3): void {
        for (let i = 0; i < count; i++) {
            this.spawnPickup();
        }
    }
}
